import React from 'react';

import {ConfirmationModalStates} from '../types';
import type {UserMenuStep} from '../types';
import {useUserMenuContent} from '../hooks/useUserMenuContent';

import {VideoAndSound} from './VideoAndSound.viewModel';
import {About} from './About.viewModel';
import {Quality} from './Quality.viewModel';
import {Layouts} from './Layouts.viewModel';
import {InMeetingSettings} from './InMeetingSettings.viewModel';
import {NerdStats} from './NerdStats.viewModel';
import {AdditionalSettings} from './AdditionalSettings.viewModel';
import {Languages} from './Languages.viewModel';
import {AccessibilitySettings} from './AccessibilitySettings.viewModel';
import {UserMenu} from '../views/UserMenu/UserMenu.view';

export const UserMenuHandler: React.FC<{
    isInMeeting: boolean;
    showLeave?: boolean;
    showQuality?: boolean;
    showMediaStatistics?: boolean;
    userMenuStep: UserMenuStep;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
    setUserMenuStep: (userMenuStep: UserMenuStep) => void;
    onBackClick: () => void;
    hasMedia?: boolean;
    confirmationState: ConfirmationModalStates;
    setConfirmationState: (confirmationState: ConfirmationModalStates) => void;
    leave?: () => void;
}> = ({
    showLeave,
    showMediaStatistics,
    showQuality,
    close,
    isInMeeting,
    userMenuStep,
    setUserMenuStep,
    onBackClick,
    hasMedia,
    confirmationState,
    setConfirmationState,
    leave,
}) => {
    switch (userMenuStep) {
        case 'video-and-sound':
            return <VideoAndSound onBackClick={onBackClick} close={close} />;

        case 'meeting-settings':
            return <InMeetingSettings onBackClick={onBackClick} />;

        case 'about':
            return <About onBackClick={onBackClick} />;

        case 'stats':
            return <NerdStats onClose={onBackClick} />;
        case 'layouts':
            return (
                <Layouts
                    onBackClick={onBackClick}
                    close={close}
                    closeConfirmation={() =>
                        setConfirmationState(ConfirmationModalStates.None)
                    }
                    openConfirmation={() =>
                        setConfirmationState(ConfirmationModalStates.Layout)
                    }
                    isConfirmationOpen={
                        confirmationState === ConfirmationModalStates.Layout
                    }
                />
            );

        case 'quality':
            return <Quality onBackClick={onBackClick} />;

        case 'accessibility':
            return <AccessibilitySettings onBackClick={onBackClick} />;

        case 'language':
            return <Languages onBackClick={onBackClick} />;

        case 'additional-settings':
            return <AdditionalSettings onBackClick={onBackClick} />;

        default:
            return (
                <UserMenuList
                    showQuality={showQuality}
                    showMediaStatistics={showMediaStatistics}
                    showLeave={showLeave}
                    close={close}
                    isInMeeting={isInMeeting}
                    setUserMenuStep={setUserMenuStep}
                    hasMedia={hasMedia}
                    leave={leave}
                />
            );
    }
};

export const UserMenuList: React.FC<{
    isInMeeting: boolean;
    showLeave?: boolean;
    showQuality?: boolean;
    showMediaStatistics?: boolean;
    setUserMenuStep: (userMenuStep: UserMenuStep) => void;
    hasMedia?: boolean;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
    leave?: () => void;
}> = ({
    isInMeeting,
    showLeave = false,
    showMediaStatistics,
    showQuality,
    setUserMenuStep,
    hasMedia,
    close,
    leave,
}) => {
    const {
        downloadLogWithName,
        canControl,
        canChangeLayout,
        isDirectMedia,
        isFeedbackEnabled,
        openFeedbackForm,
        pluginActions,
    } = useUserMenuContent({
        isInMeeting,
    });

    return (
        <UserMenu
            showLeave={showLeave}
            showMediaStatistics={showMediaStatistics}
            showQuality={showQuality}
            onLeave={leave}
            hasMedia={hasMedia}
            isDirectMedia={isDirectMedia}
            isFeedbackEnabled={isFeedbackEnabled}
            isInMeeting={isInMeeting}
            canChangeLayout={canChangeLayout}
            canControl={canControl}
            setUserMenuStep={setUserMenuStep}
            pluginActions={pluginActions}
            close={close}
            downloadLogWithName={downloadLogWithName}
            openFeedbackForm={openFeedbackForm}
        />
    );
};
